import type { TrackMeta } from './types.js';

/**
 * A single tracked event, as seen by the dev/PO tracking inspector: the typed
 * `track()` call plus the GA4 payload the adapter produced for the dataLayer.
 */
export interface TrackingInspectorEvent {
  /** Monotonic id assigned on emit, unique for the session — a stable list key. */
  id: number;
  /** The `/collect` dedup id sent for this event. */
  event_id: string;
  name: string;
  params: Record<string, unknown>;
  meta: TrackMeta;
  timestamp: number;
  /** Post-adapter GA4 payload pushed to the dataLayer, or `null` when a mapper suppressed the event. */
  mapped: Record<string, unknown> | null;
}

type Listener = (event: TrackingInspectorEvent) => void;

const RING_SIZE = 50;
const listeners = new Set<Listener>();
const ring: TrackingInspectorEvent[] = [];
let seq = 0;

/** Record a tracked event: append to the bounded ring and notify live subscribers. */
export function emitTrackingEvent(event: Omit<TrackingInspectorEvent, 'id'>): void {
  const full: TrackingInspectorEvent = { id: seq++, ...event };
  ring.push(full);
  if (ring.length > RING_SIZE) {
    ring.shift();
  }
  for (const listener of listeners) {
    listener(full);
  }
}

/** Subscribe to live tracked events. Returns an unsubscribe function. */
export function subscribeTrackingEvents(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Recently tracked events (oldest first), to seed an inspector that mounts late. */
export function getRecentTrackingEvents(): readonly TrackingInspectorEvent[] {
  return [...ring];
}

/** @internal — test-only reset */
export function _resetInspector(): void {
  listeners.clear();
  ring.length = 0;
  seq = 0;
}
